import { useEffect, useLayoutEffect, useRef, type ReactNode } from 'react';
import styled from 'styled-components';

export const GROW_MS = 440;
export const EASING = 'cubic-bezier(0.16, 1, 0.3, 1)';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

export interface ExpandingOverlayProps {
  /**
   * On-screen rect the overlay should grow out of / shrink back into
   * (e.g. the Video tab's poster thumbnail). When set, the overlay
   * animates between this rect and its full size (FLIP); otherwise it
   * fades.
   */
  origin?: DOMRect | null;
  /**
   * `true` plays the enter (grow) animation; flipping to `false` plays
   * the exit (shrink) animation, after which `onExitComplete` fires.
   */
  open: boolean;
  /** Called once the exit animation finishes — the parent unmounts then. */
  onExitComplete?: () => void;
  children: ReactNode;
}

/**
 * Fills the sidebar region with the overlay and animates it growing out
 * of `origin` to full size on enter, then shrinking back into `origin`
 * on exit, via a FLIP (render at the final size; transform between the
 * two rects). Only `transform`/`opacity` animate, so the content keeps
 * its real full-size box throughout (no layout thrash — important for the
 * embedded video.js player).
 *
 * Exit is driven by the `open` prop rather than unmounting: the parent
 * keeps this mounted until `onExitComplete` fires so the shrink is
 * actually visible.
 */
export const ExpandingOverlay = ({
  origin,
  open,
  onExitComplete,
  children,
}: ExpandingOverlayProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const exitFiredRef = useRef(false);
  // Tracks the previous `open` so the animation only fires on an actual
  // open↔close transition — not on unrelated parent re-renders.
  const prevOpenRef = useRef<boolean | null>(null);
  // Latest `onExitComplete` read via a ref so it can stay OUT of the
  // layout-effect deps; both apps pass it as a fresh inline arrow each
  // render, which would otherwise re-run the effect (replaying the FLIP)
  // on every chatbot message / query refetch while expanded.
  const onExitCompleteRef = useRef(onExitComplete);
  useEffect(() => {
    onExitCompleteRef.current = onExitComplete;
  });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const prevOpen = prevOpenRef.current;
    prevOpenRef.current = open;
    // Ignore re-renders that don't change `open` (e.g. origin identity
    // churn): only animate on a real transition or the first mount.
    if (prevOpen === open) return;
    const reduce = prefersReducedMotion();

    if (open) {
      // Entering — allow a future exit to fire again.
      exitFiredRef.current = false;

      if (reduce) {
        el.style.opacity = '1';
        el.style.transform = 'none';
        return;
      }

      if (!origin) {
        // No thumbnail rect: simple fade-in.
        el.style.transition = 'none';
        el.style.opacity = '0';
        void el.offsetWidth;
        el.style.transition = `opacity ${GROW_MS}ms ${EASING}`;
        el.style.opacity = '1';
        return;
      }

      const target = el.getBoundingClientRect();
      if (target.width === 0 || target.height === 0) return;
      const { sx, sy, tx, ty } = deltaTo(origin, target);

      // First frame: place + shrink onto the thumbnail (no transition).
      el.style.transformOrigin = 'top left';
      el.style.transition = 'none';
      el.style.opacity = '0.4';
      el.style.transform = `translate(${tx}px, ${ty}px) scale(${sx}, ${sy})`;
      // Commit the inverted transform before animating back.
      void el.offsetWidth;
      el.style.transition = `transform ${GROW_MS}ms ${EASING}, opacity ${GROW_MS}ms ${EASING}`;
      el.style.opacity = '1';
      el.style.transform = 'translate(0, 0) scale(1, 1)';
      return;
    }

    // Exiting.
    if (reduce || !origin) {
      // No animation to wait on. Defer so we don't call the parent's
      // setState synchronously inside this layout effect (which would
      // unmount mid-commit).
      queueMicrotask(() => onExitCompleteRef.current?.());
      return;
    }
    const target = el.getBoundingClientRect();
    const { sx, sy, tx, ty } = deltaTo(origin, target);
    el.style.transformOrigin = 'top left';
    el.style.transition = `transform ${GROW_MS}ms ${EASING}, opacity ${GROW_MS}ms ${EASING}`;
    el.style.opacity = '0.2';
    el.style.transform = `translate(${tx}px, ${ty}px) scale(${sx}, ${sy})`;
    // Completion handled by onTransitionEnd below.
  }, [open, origin]);

  return (
    <StyledOverlayFill
      ref={ref}
      onTransitionEnd={(e) => {
        // Only the overlay's own transition counts — ignore transitions
        // bubbling up from children (e.g. the video.js control-bar fade),
        // which would otherwise fire the exit early and unmount mid-shrink.
        if (e.target !== e.currentTarget) return;
        if (open || exitFiredRef.current) return;
        exitFiredRef.current = true;
        onExitCompleteRef.current?.();
      }}
    >
      {children}
    </StyledOverlayFill>
  );
};

/** Transform that maps the element's current `target` rect onto `origin`. */
const deltaTo = (origin: DOMRect, target: DOMRect) => ({
  sx: origin.width / target.width,
  sy: origin.height / target.height,
  tx: origin.left - target.left,
  ty: origin.top - target.top,
});

const StyledOverlayFill = styled.div`
  height: 100%;
  width: 100%;
  min-height: 0;
  overflow: hidden;
`;
